import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formatReportDate } from '../utils/dateUtils';
import { pdf } from '@react-pdf/renderer';
import LineInspectionReportPDF from '../components/LineInspectionReportPDF';
import MigrosLineInspectionReportPDF from '../components/MigrosLineInspectionReportPDF';
import { useAllowedReportTemplates } from '../utils/reportTemplates';

type PdfTemplate = 'qa-mate' | 'migros';

interface Photo {
  id: string;
  image_url: string;
  caption: string;
  label: string;
  is_required: boolean;
  description?: string;
}

interface ChecklistItem {
  id: string;
  category: string;
  title?: string;
  description: string;
  status: 'PASS' | 'FAIL' | 'NA' | 'pass' | 'fail' | 'na';
  remarks: string;
  evidence_images: string[];
}

interface Defect {
  id: string;
  type: string;
  title?: string;
  description?: string;
  category?: string;
  severity: 'MINOR' | 'MAJOR' | 'CRITICAL';
  count: number;
  evidence_images: string[];
}

interface VerificationItem {
  name?: string;
  title?: string;
  value: string;
  unit?: string;
  units?: string;
  measurement_unit?: string;
}

interface InspectionDetail {
  id: string;
  basis: string;
  ref_number: string;
  fabric_type: string;
  sampling_method: string;
  lot_size: number;
  sample_size: number;
  status: string;
  result: 'PASS' | 'FAIL' | 'PENDING';
  inspector_name: string;
  inspector_signature_url: string;
  factory_rep_signature_url: string;
  remarks: string;
  created_at: string;
  checklists: ChecklistItem[];
  defects: Defect[];
  photos: Photo[];
  verifications?: VerificationItem[];
  verification_items?: VerificationItem[];
  process?: { id: string; name: string } | null;
  process_id?: string;
  additional_documents?: string[];
}

const LineInspectionReportDetail: React.FC = () => {
  const { id } = useParams();
  const { token } = useAuth();
  const [inspection, setInspection] = useState<InspectionDetail | null>(null);
  const [fabricTypes, setFabricTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const zoomContainerRef = useRef<HTMLDivElement>(null);
  const [selectedDocument, setSelectedDocument] = useState<{name: string, url: string} | null>(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [pdfStatusIndex, setPdfStatusIndex] = useState(0);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [downloadingImages, setDownloadingImages] = useState(false);
  const [imageStatusIndex, setImageStatusIndex] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState<PdfTemplate>('qa-mate');
  const [isTemplateDropdownOpen, setIsTemplateDropdownOpen] = useState(false);
  const templateDropdownRef = useRef<HTMLDivElement>(null);

  const openPreview = useCallback((url: string | null) => {
    setZoom(1);
    setPreviewImage(url);
  }, []);

  useEffect(() => {
    const el = zoomContainerRef.current;
    if (!el || !previewImage) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      setZoom((prev: number) => Math.min(Math.max(0.25, prev - e.deltaY * 0.001), 5));
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [previewImage]);

  useEffect(() => {
    const fetchInspection = async () => {
      try {
        const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
        const response = await fetch(`${baseUrl}/api/v1/line-inspections/${id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await response.json();
        if (data.success) {
          setInspection(data.data);
        }
      } catch (err) {
        console.error('Failed to fetch inspection detail:', err);
      } finally {
        setLoading(false);
      }
    };

    const fetchFabricTypes = async () => {
      try {
        const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
        const response = await fetch(`${baseUrl}/api/v1/fabric-product-categories`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await response.json();
        if (data.success) {
          setFabricTypes(data.data);
        }
      } catch (err) {
        console.error('Failed to fetch fabric types:', err);
      }
    };

    if (token && id) {
      fetchInspection();
      fetchFabricTypes();
    }
  }, [id, token]);

  const getFabricTypeName = (id: string) => {
    const type = fabricTypes.find(t => t.id === id);
    return type ? type.title : id;
  };

  const templateOptions: { value: PdfTemplate; name: string; icon: string; iconBg: string; iconColor: string; description: string }[] = [
    {
      value: 'qa-mate',
      name: 'QA Mate Template',
      icon: 'verified',
      iconBg: 'bg-blue-100',
      iconColor: 'text-[#136bce]',
      description: 'Default QA Mate line inspection report layout.',
    },
    {
      value: 'migros',
      name: 'Migros Template',
      icon: 'inventory_2',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      description: 'Migros Asia — Final Random Inspection (Textile/Hometextile) Version-1 layout, adapted to line inspection data.',
    },
  ];
  const allowedTemplates = useAllowedReportTemplates();
  const visibleTemplateOptions = templateOptions.filter(o => allowedTemplates.includes(o.value));
  const selectedTemplateOption =
    visibleTemplateOptions.find(t => t.value === selectedTemplate) ??
    visibleTemplateOptions[0] ??
    templateOptions[0];
  useEffect(() => {
    if (visibleTemplateOptions.length && !visibleTemplateOptions.some(o => o.value === selectedTemplate)) {
      setSelectedTemplate(visibleTemplateOptions[0].value);
    }
  }, [visibleTemplateOptions, selectedTemplate]);

  const pdfStatusMessages = [
    'Preparing line inspection data…',
    'Securing report metadata…',
    'Fetching checklist & defect images…',
    'Compiling evaluation findings…',
    'Embedding signatures…',
    'Rendering high-quality pages…',
    'Almost there — finalizing your PDF…',
  ];

  const imageStatusMessages = [
    'Collecting image list…',
    'Fetching inspection photos…',
    'Fetching checklist evidence…',
    'Fetching defect evidence…',
    'Fetching additional captures…',
    'Compressing into ZIP archive…',
    'Almost done — finalizing your download…',
  ];

  useEffect(() => {
    if (!generatingPDF) { setPdfStatusIndex(0); return; }
    const interval = setInterval(() => setPdfStatusIndex(i => (i + 1) % pdfStatusMessages.length), 3000);
    return () => clearInterval(interval);
  }, [generatingPDF]);

  useEffect(() => {
    if (!downloadingImages) { setImageStatusIndex(0); return; }
    const interval = setInterval(() => setImageStatusIndex(i => (i + 1) % imageStatusMessages.length), 2500);
    return () => clearInterval(interval);
  }, [downloadingImages]);

  useEffect(() => {
    if (!isTemplateDropdownOpen) return;
    const onClick = (e: MouseEvent) => {
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(e.target as Node))
        setIsTemplateDropdownOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [isTemplateDropdownOpen]);

  const toDataUrl = async (url: string): Promise<string> => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
    let resolved = url;
    if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('data:')) {
      resolved = `${baseUrl}${url}`;
    }
    if (resolved.includes('storage.googleapis.com')) {
      resolved = `${baseUrl}/api/v1/proxy-image?url=${encodeURIComponent(resolved)}`;
    }
    return new Promise((resolve, reject) => {
      fetch(resolved)
        .then(r => r.blob())
        .then(blob => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        })
        .catch(reject);
    });
  };

  const handleGeneratePDF = async (template: PdfTemplate) => {
    if (!inspection) return;
    setPdfModalOpen(false);
    setGeneratingPDF(true);
    try {
      const cache = new Map<string, string>();
      const resolveUrl = async (url: string | null | undefined): Promise<string> => {
        if (!url) return '';
        if (cache.has(url)) return cache.get(url)!;
        try { const r = await toDataUrl(url); cache.set(url, r); return r; }
        catch { return ''; }
      };
      const resolveArr = async (urls: string[] | undefined): Promise<string[]> => {
        if (!urls || urls.length === 0) return [];
        return Promise.all(urls.map(u => resolveUrl(u)));
      };

      const clone = JSON.parse(JSON.stringify(inspection));
      clone.fabric_type_name = getFabricTypeName(clone.fabric_type);

      // Resolve checklist evidence images
      for (const item of clone.checklists || []) {
        item.evidence_images = await resolveArr(item.evidence_images);
      }
      // Resolve defect evidence images
      for (const defect of clone.defects || []) {
        defect.evidence_images = await resolveArr(defect.evidence_images);
      }
      // Resolve photos
      for (const photo of clone.photos || []) {
        photo.image_url = await resolveUrl(photo.image_url);
      }
      // Resolve signatures
      clone.inspector_signature_url = await resolveUrl(clone.inspector_signature_url);
      clone.factory_rep_signature_url = await resolveUrl(clone.factory_rep_signature_url);

      const docElement = template === 'migros'
        ? <MigrosLineInspectionReportPDF inspection={clone} />
        : <LineInspectionReportPDF inspection={clone} />;

      const blob = await pdf(docElement).toBlob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const suffix = inspection.id.slice(0, 8).toUpperCase();
      link.download = template === 'migros'
        ? `Migros-Line-Inspection-${suffix}.pdf`
        : `Line-Inspection-${suffix}.pdf`;
      link.click();
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleDownload = () => {
    window.print();
  };

  const handleDownloadImages = async () => {
    if (!inspection) return;
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

    const fetchBlob = async (url: string): Promise<Blob | null> => {
      if (!url) return null;
      let abs = url;
      if (!/^(https?:|blob:|data:)/.test(abs)) abs = `${baseUrl}${abs.startsWith('/') ? '' : '/'}${abs}`;
      const isGcs = abs.includes('storage.googleapis.com');
      const fetchUrl = isGcs ? `${baseUrl}/api/v1/proxy-image?url=${encodeURIComponent(abs)}` : abs;
      try {
        const res = await fetch(fetchUrl, { mode: 'cors' });
        if (res.ok) return await res.blob();
      } catch {}
      return null;
    };

    const getExt = (url: string, blob: Blob) => {
      const m = url.match(/\.(\w{2,5})(\?|$)/);
      if (m) return m[1].toLowerCase();
      if (blob.type.includes('jpeg') || blob.type.includes('jpg')) return 'jpg';
      if (blob.type.includes('png')) return 'png';
      if (blob.type.includes('webp')) return 'webp';
      return 'jpg';
    };

    const requiredPhotos = inspection.photos.filter(p => p.is_required === true);
    const additionalPhotos = inspection.photos.filter(p => p.is_required !== true);
    const checklistImgs = inspection.checklists.flatMap(c => c.evidence_images || []).filter(Boolean);
    const defectImgs = inspection.defects.flatMap(d => d.evidence_images || []).filter(Boolean);

    const total = requiredPhotos.length + additionalPhotos.length + checklistImgs.length + defectImgs.length;
    if (total === 0) { alert('No images found to download.'); return; }

    setDownloadingImages(true);
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      if (requiredPhotos.length > 0) {
        const folder = zip.folder('Inspection_Photos')!;
        for (let i = 0; i < requiredPhotos.length; i++) {
          const blob = await fetchBlob(requiredPhotos[i].image_url);
          if (blob) {
            const ext = getExt(requiredPhotos[i].image_url, blob);
            const lbl = (requiredPhotos[i].label || requiredPhotos[i].caption || `Photo_${i + 1}`).replace(/[^a-zA-Z0-9_\-]/g, '_');
            folder.file(`${String(i + 1).padStart(2, '0')}_${lbl}.${ext}`, blob);
          }
        }
      }
      if (additionalPhotos.length > 0) {
        const folder = zip.folder('Additional_Photos')!;
        for (let i = 0; i < additionalPhotos.length; i++) {
          const blob = await fetchBlob(additionalPhotos[i].image_url);
          if (blob) {
            const ext = getExt(additionalPhotos[i].image_url, blob);
            const lbl = (additionalPhotos[i].label || `Photo_${i + 1}`).replace(/[^a-zA-Z0-9_\-]/g, '_');
            folder.file(`${String(i + 1).padStart(2, '0')}_${lbl}.${ext}`, blob);
          }
        }
      }
      if (checklistImgs.length > 0) {
        const folder = zip.folder('Checklist_Evidence')!;
        for (let i = 0; i < checklistImgs.length; i++) {
          const blob = await fetchBlob(checklistImgs[i]);
          if (blob) folder.file(`${String(i + 1).padStart(2, '0')}_Checklist.${getExt(checklistImgs[i], blob)}`, blob);
        }
      }
      if (defectImgs.length > 0) {
        const folder = zip.folder('Defect_Evidence')!;
        for (let i = 0; i < defectImgs.length; i++) {
          const blob = await fetchBlob(defectImgs[i]);
          if (blob) folder.file(`${String(i + 1).padStart(2, '0')}_Defect.${getExt(defectImgs[i], blob)}`, blob);
        }
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(zipBlob);
      a.download = `Line-Inspection-Images-${inspection.id.slice(0, 8).toUpperCase()}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err: any) {
      console.error('Image download failed:', err);
      alert(`Image download failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setDownloadingImages(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#136bce] border-t-transparent"></div>
        <p className="text-slate-500 font-medium">Loading inspection report...</p>
      </div>
    );
  }

  if (!inspection) {
    return (
      <div className="text-center py-20">
        <h2 className="text-xl font-bold text-slate-800">Report Not Found</h2>
        <p className="text-slate-500 mt-2">The inspection report you are looking for does not exist.</p>
        <Link to="/line-inspection-reports" className="mt-4 inline-block text-[#136bce] font-bold">Back to Reports</Link>
      </div>
    );
  }

  const passCount = inspection.checklists.filter(c => c.status?.toUpperCase() === 'PASS').length;
  const failCount = inspection.checklists.filter(c => c.status?.toUpperCase() === 'FAIL').length;
  const totalDefects = inspection.defects.reduce((acc, d) => acc + d.count, 0);
  const requiredPhotos = inspection.photos.filter(p => p.is_required === true);
  const additionalPhotos = inspection.photos.filter(p => p.is_required !== true);

  return (
    <div className="mx-auto w-full max-w-full animate-in fade-in duration-500 print:animate-none print:p-0 space-y-8">
      {/* Breadcrumbs - Hidden in Print */}
      <nav aria-label="Breadcrumb" className="flex text-sm text-slate-500 print:hidden">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link to="/dashboard" className="inline-flex items-center hover:text-slate-900 transition-colors">
              <span className="material-symbols-outlined text-[18px] mr-2">dashboard</span>
              Dashboard
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <span className="material-symbols-outlined text-slate-400 text-[18px]">chevron_right</span>
              <Link to="/line-inspection-reports" className="ml-1 hover:text-slate-900 md:ml-2 transition-colors">Line Inspection</Link>
            </div>
          </li>
          <li aria-current="page">
            <div className="flex items-center">
              <span className="material-symbols-outlined text-slate-400 text-[18px]">chevron_right</span>
              <span className="ml-1 font-medium text-[#136bce] md:ml-2">Report #{id?.substring(0, 8).toUpperCase()}</span>
            </div>
          </li>
        </ol>
      </nav>

      {/* Title & Actions */}
      <div className="flex flex-col items-start sm:flex-row sm:items-center justify-between gap-4 print:mb-10">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 leading-tight">Line Inspection Report</h1>
          <p className="text-sm text-slate-500 mt-1 print:block hidden">Generated by QA MATE Inspect Pro</p>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto print:hidden">
          <button
            onClick={() => setPdfModalOpen(true)}
            disabled={generatingPDF}
            className="flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md transition-all hover:bg-emerald-700 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed flex-1 sm:flex-none"
          >
            {generatingPDF ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <span className="material-symbols-outlined text-[20px]">picture_as_pdf</span>
            )}
            {generatingPDF ? 'Generating...' : 'Generate PDF'}
          </button>
          <button
            onClick={handleDownloadImages}
            disabled={downloadingImages}
            className="flex items-center justify-center gap-2 rounded-lg bg-violet-600 px-5 py-2.5 text-sm font-bold text-white shadow-md transition-all hover:bg-violet-700 active:scale-95 flex-1 sm:flex-none disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <span className="material-symbols-outlined text-[20px]">{downloadingImages ? 'hourglass_top' : 'photo_library'}</span>
            {downloadingImages ? 'Zipping…' : 'Download Images'}
          </button>
          <button
            onClick={handleDownload}
            className="flex items-center justify-center gap-2 rounded-lg bg-[#136bce] px-5 py-2.5 text-sm font-bold text-white shadow-md transition-all hover:bg-blue-700 active:scale-95 flex-1 sm:flex-none"
          >
            <span className="material-symbols-outlined text-[20px]">download</span>
            Download Report
          </button>
        </div>
      </div>

      {/* Standardized Result Status Banner */}
      <div className="bg-white rounded-2xl shadow-md border border-slate-200 overflow-hidden flex flex-col md:flex-row h-auto md:h-44">
        <div className={`md:w-1/3 text-white p-6 flex flex-col items-center justify-center text-center relative overflow-hidden ${inspection.result?.toUpperCase() === 'PASS' ? 'bg-emerald-500' : inspection.result?.toUpperCase() === 'FAIL' ? 'bg-red-500' : 'bg-slate-500'}`}>
          <div className={`absolute inset-0 bg-gradient-to-br opacity-100 z-0 ${inspection.result?.toUpperCase() === 'PASS' ? 'from-emerald-400 to-emerald-600' : inspection.result?.toUpperCase() === 'FAIL' ? 'from-red-400 to-red-600' : 'from-slate-400 to-slate-600'}`}></div>
          <span className="material-symbols-outlined absolute -right-6 -bottom-6 text-[140px] opacity-20 rotate-12 select-none z-0">{inspection.result?.toUpperCase() === 'PASS' ? 'check_circle' : inspection.result?.toUpperCase() === 'FAIL' ? 'cancel' : 'pending'}</span>
          <div className="relative z-10 w-full flex flex-col items-center justify-center h-full">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase mb-2 shadow-sm ring-1 ring-white/30">
              <span className={`w-1.5 h-1.5 rounded-full bg-white ${inspection.status === 'IN_PROGRESS' ? 'animate-pulse' : ''}`}></span>
              {inspection.status === 'COMPLETED' ? 'Final Result' : 'Preliminary'}
            </div>
            <h2 className="text-5xl font-black tracking-tighter drop-shadow-sm leading-none mb-1">{inspection.result?.toUpperCase() || 'PENDING'}</h2>
            <p className="text-white/80 text-xs font-medium opacity-90">{inspection.result?.toUpperCase() === 'PASS' ? 'Meets quality standards' : inspection.result?.toUpperCase() === 'FAIL' ? 'Does not meet standards' : 'Inspection in progress'}</p>
          </div>
        </div>
        <div className="md:w-2/3 p-6 flex items-center justify-around bg-white relative">
          <div className="text-center group">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Total Defects</p>
            <div className="flex items-baseline justify-center gap-2">
              <p className={`text-4xl font-black transition-colors ${totalDefects > 0 ? 'text-red-500' : 'text-slate-800'}`}>{totalDefects || 0}</p>
              <span className="text-sm font-bold text-slate-400">found</span>
            </div>
          </div>
          <div className="w-px h-20 bg-slate-100 mx-4"></div>
          <div className="text-center group">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Checklist Score</p>
            <div className="flex items-baseline justify-center gap-2">
              <p className="text-4xl font-black text-slate-800 group-hover:text-[#136bce] transition-colors">{passCount}/{inspection.checklists.length}</p>
              <span className="text-sm font-bold text-slate-400">passed</span>
            </div>
          </div>
          <div className="w-px h-20 bg-slate-100 mx-4"></div>
          <div className="text-center group">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Sample Size</p>
            <div className="flex items-baseline justify-center gap-2">
              <p className="text-4xl font-black text-slate-800 group-hover:text-amber-500 transition-colors">{inspection.sample_size}</p>
              <span className="text-sm font-bold text-slate-400">units</span>
            </div>
          </div>
        </div>
      </div>

      <main className="space-y-8 print:space-y-10">
        {/* Inspection Summary */}
        <section className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden w-full break-inside-avoid">
          <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4 flex items-center justify-between">
            <h2 className="font-bold text-slate-800 flex items-center gap-2 text-base">
              <span className="material-symbols-outlined text-[#136bce]">summarize</span>
              Inspection Details
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 gap-x-8 gap-y-6 md:grid-cols-4 lg:grid-cols-5">
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Inspection ID</span>
                <span className="text-sm font-bold text-slate-900">{inspection.id.substring(0, 8).toUpperCase()}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">PO/Batch/Shift</span>
                <span className="text-sm font-bold text-slate-900">{inspection.basis}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Production Process</span>
                <span className="text-sm font-bold text-slate-900">{inspection.process?.name || 'N/A'}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Ref Number</span>
                <span className="text-sm font-bold text-slate-900">{inspection.ref_number}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Lot Size</span>
                <span className="text-sm font-bold text-slate-900">{inspection.lot_size.toLocaleString()}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Sample Size</span>
                <span className="text-sm font-bold text-slate-900">{inspection.sample_size}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Fabric Type</span>
                <span className="text-sm font-bold text-slate-900">{getFabricTypeName(inspection.fabric_type) || 'N/A'}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Sampling Method</span>
                <span className="text-sm font-bold text-slate-900">{inspection.sampling_method || 'N/A'}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Inspector</span>
                <div className="flex items-center gap-2">
                  <div className="h-5 w-5 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600">
                    {inspection.inspector_name.split(' ').map(n=>n[0]).join('').substring(0,2)}
                  </div>
                  <span className="text-sm font-bold text-slate-900">{inspection.inspector_name}</span>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Date</span>
                <span className="text-sm font-bold text-slate-900">{formatReportDate(inspection.created_at)}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Additional Documents */}
        {Array.isArray(inspection.additional_documents) && inspection.additional_documents.length > 0 && (
          <section className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden w-full break-inside-avoid">
            <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4 flex items-center justify-between">
              <h2 className="font-bold text-slate-800 flex items-center gap-2 text-base">
                <span className="material-symbols-outlined text-[#136bce]">attach_file</span>
                Additional Documents
              </h2>
              <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                {inspection.additional_documents.length} file(s)
              </span>
            </div>
            <div className="p-6">
              <div className="flex flex-wrap gap-4">
                {inspection.additional_documents.map((url, idx) => {
                  const isPdf = /\.(pdf)($|\?)/i.test(url);
                  let fileName = `Document ${idx + 1}`;
                  try {
                    const urlObj = new URL(url);
                    fileName = urlObj.pathname.split('/').pop() || fileName;
                  } catch (e) {
                    fileName = url.split('?')[0].split('/').pop() || fileName;
                  }

                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => isPdf ? setSelectedDocument({ name: fileName, url }) : openPreview(url)}
                      className={`group relative h-28 w-28 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50 hover:border-[#136bce] transition-colors ${isPdf ? 'flex items-center justify-center cursor-pointer' : 'cursor-zoom-in'}`}
                    >
                      {isPdf ? (
                        <div className="flex flex-col items-center justify-center h-full w-full bg-slate-50 text-red-500">
                          <span className="material-symbols-outlined text-4xl mb-1">picture_as_pdf</span>
                          <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest px-2 truncate w-full group-hover:text-red-500 transition-colors">PDF Doc</span>
                        </div>
                      ) : (
                        <img
                          src={url}
                          alt={`Additional document ${idx + 1}`}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      )}
                      {isPdf && (
                        <div className="absolute top-2 right-2 bg-red-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded shadow-sm uppercase tracking-widest z-10">
                          PDF
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Checklist */}
        <section className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden w-full break-inside-avoid">
          <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4 flex items-center justify-between">
            <h2 className="font-bold text-slate-800 flex items-center gap-2 text-base">
              <span className="material-symbols-outlined text-[#136bce]">fact_check</span>
              Checklist
            </h2>
            <div className="text-xs font-bold text-slate-500 uppercase tracking-widest">
              <span className="text-emerald-600">{passCount} Passed</span> • <span className="text-red-600">{failCount} Failed</span>
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {inspection.checklists.map((item, idx) => {
                const status = item.status?.toUpperCase();
                const isFail = status === 'FAIL';
                const isPass = status === 'PASS';

                return (
                <div key={idx} className={`${isFail ? 'bg-red-50/40 p-6' : 'p-4 px-6'} flex flex-col group transition-colors hover:bg-slate-50`}>
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-4">
                            <div className={`p-2 rounded-lg ${isPass ? 'bg-emerald-50 text-emerald-600' : isFail ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-600'}`}>
                                <span className="material-symbols-outlined text-xl">{isFail ? 'texture' : 'label'}</span>
                            </div>
                            <div>
                                <h4 className="font-bold text-slate-800 text-sm">{item.title || item.category}</h4>
                                <p className="text-xs text-slate-500 font-medium">{item.description}</p>
                            </div>
                        </div>
                        <span className={`inline-flex items-center rounded-full px-3 py-1 text-[10px] font-bold border uppercase tracking-wider ${isPass ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : isFail ? 'bg-red-100 text-red-800 border-red-200' : 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                            {item.status}
                        </span>
                    </div>

                    {(item.evidence_images?.length > 0 || item.remarks) && (
                        <div className="pl-0 md:pl-14 mt-4">
                            {item.evidence_images && item.evidence_images.length > 0 && (
                                <div className="flex flex-wrap gap-4 mb-3">
                                    {item.evidence_images.map((url, i) => (
                                        <div
                                            key={i}
                                            className="relative h-28 w-28 rounded-lg overflow-hidden border border-slate-200 bg-slate-100 shadow-sm shrink-0 cursor-zoom-in"
                                            onClick={() => openPreview(url)}
                                        >
                                            <img alt="Evidence" className="h-full w-full object-cover" src={url}/>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {item.remarks && (
                                <div className="flex flex-col gap-1">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Remarks</span>
                                    <p className="text-sm text-slate-600 leading-relaxed font-medium">{item.remarks}</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                );
            })}
          </div>
        </section>

        {/* Measurements & Verifications */}
        {(() => {
          const verifications = inspection.verifications || inspection.verification_items || [];
          if (verifications.length === 0) return null;
          return (
            <section className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden w-full break-inside-avoid">
              <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4 flex items-center justify-between">
                <h2 className="font-bold text-slate-800 flex items-center gap-2 text-base">
                  <span className="material-symbols-outlined text-teal-500">straighten</span>
                  Measurements & Verifications
                </h2>
                <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">{verifications.length} Items</span>
              </div>
              <div className="divide-y divide-slate-100">
                {verifications.map((v, idx) => {
                  const name = v.name || v.title || '';
                  const value = v.value || '';
                  // Look for different possible unit keys for resilience
                  const unit = v.unit || v.units || v.measurement_unit || '';
                  const displayValue = value ? (unit ? `${value} ${unit}` : value) : '-';
                  return (
                    <div key={idx} className="flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors">
                      <span className="text-sm font-bold text-slate-800">{name}</span>
                      <span className="text-sm font-medium text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1">{displayValue}</span>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })()}

        {/* Recorded Defects */}
        {inspection.defects.length > 0 && (
            <section className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden w-full break-inside-avoid">
                <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4 flex items-center justify-between">
                    <h2 className="font-bold text-slate-800 flex items-center gap-2 text-base">
                    <span className="material-symbols-outlined text-[#136bce]">bug_report</span>
                    Recorded Defects
                    </h2>
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Total Count: <span className="text-slate-900">{totalDefects}</span></span>
                </div>
                <div className="p-6 space-y-6 bg-slate-50/30 print:bg-transparent">
                    {inspection.defects.map((defect, idx) => (
                        <div key={idx} className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm print:shadow-none break-inside-avoid">
                            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4 md:items-start mb-6 border-b border-slate-50 pb-6">
                                <div>
                                    <h3 className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">Defect Title</h3>
                                    <div className="text-lg font-bold text-slate-900">{defect.title || '-'}</div>
                                </div>
                                <div>
                                    <h3 className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">Defect Category</h3>
                                    <div className="text-sm font-bold text-slate-900 uppercase tracking-wide">{defect.category || '-'}</div>
                                </div>
                                <div>
                                    <h3 className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">Defect Type</h3>
                                    <div className="text-sm font-bold text-slate-900 uppercase tracking-wide">{defect.type || '-'}</div>
                                </div>
                                <div className="flex flex-col gap-3">
                                    <div>
                                        <h3 className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">Count</h3>
                                        <div className="text-base font-bold text-slate-900">{defect.count}</div>
                                    </div>
                                    <div>
                                        <h3 className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">Severity</h3>
                                        <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-[10px] font-bold ring-1 ring-inset uppercase tracking-wider ${defect.severity?.toUpperCase() === 'CRITICAL' ? 'bg-red-50 text-red-700 ring-red-600/20' : defect.severity?.toUpperCase() === 'MAJOR' ? 'bg-amber-50 text-amber-700 ring-amber-600/20' : 'bg-blue-50 text-blue-700 ring-blue-600/20'}`}>
                                            {defect.severity}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            {defect.description && (
                                <div className="mb-5">
                                    <h4 className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1">Description</h4>
                                    <p className="text-sm text-slate-700 italic font-medium leading-relaxed">&ldquo;{defect.description}&rdquo;</p>
                                </div>
                            )}
                            {defect.evidence_images && defect.evidence_images.length > 0 && (
                                <div className="mb-5">
                                    <h4 className="text-[10px] font-bold text-slate-400 mb-3 uppercase tracking-widest">Evidence Photos:</h4>
                                    <div className="flex flex-wrap gap-4">
                                        {defect.evidence_images.map((url, i) => (
                                            <div 
                                                key={i} 
                                                className="h-28 w-28 overflow-hidden rounded-lg border border-slate-200 bg-slate-50 shadow-sm print:border cursor-zoom-in"
                                                onClick={() => openPreview(url)}
                                            >
                                                <img alt="Defect thumb" className="h-full w-full object-cover transition-transform hover:scale-110" src={url} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </section>
        )}

        {/* Photos Section */}
        {requiredPhotos.length > 0 && (
            <section className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden w-full break-inside-avoid">
                <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
                    <h2 className="font-bold text-slate-800 flex items-center gap-2 text-base">
                        <span className="material-symbols-outlined text-[#136bce]">image</span>
                        Inspection Photos
                    </h2>
                </div>
                <div className="p-6">
                    <div className="flex flex-wrap gap-6">
                        {requiredPhotos.map((photo, i) => (
                            <div key={i} className="flex flex-col gap-2 w-full sm:w-64">
                                <div
                                    className="h-44 w-full overflow-hidden rounded-xl border border-slate-200 bg-slate-50 shadow-sm cursor-zoom-in group"
                                    onClick={() => openPreview(photo.image_url)}
                                >
                                    <img src={photo.image_url} alt={photo.label || photo.caption} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                                </div>
                                <p className="text-xs font-bold text-slate-500 text-center uppercase tracking-wide">{photo.label || photo.caption}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        )}

        {/* Additional Photos Section */}
        {additionalPhotos.length > 0 && (
            <section className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden w-full break-inside-avoid">
                <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
                    <h2 className="font-bold text-slate-800 flex items-center gap-2 text-base">
                        <span className="material-symbols-outlined text-teal-500">add_photo_alternate</span>
                        Additional Captures
                    </h2>
                </div>
                <div className="p-6">
                    <div className="flex flex-wrap gap-6">
                        {additionalPhotos.map((photo, i) => (
                            <div key={i} className="flex flex-col gap-2 w-full sm:w-64">
                                <div
                                    className="h-44 w-full overflow-hidden rounded-xl border border-slate-200 bg-slate-50 shadow-sm cursor-zoom-in group"
                                    onClick={() => openPreview(photo.image_url)}
                                >
                                    <img src={photo.image_url} alt={photo.label} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                                </div>
                                <p className="text-xs font-bold text-slate-500 text-center uppercase tracking-wide">{photo.label}</p>
                                {photo.description && (
                                    <p className="text-xs text-slate-400 text-center">{photo.description}</p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        )}

        {/* Final Remarks */}
        <section className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden w-full break-inside-avoid">
          <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4 flex items-center justify-between">
            <h2 className="font-bold text-slate-800 flex items-center gap-2 text-base">
              <span className="material-symbols-outlined text-[#136bce]">rate_review</span>
              Overall Remarks
            </h2>
          </div>
          <div className="p-8 space-y-8">
            {inspection.remarks && (
                <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Remark Description</label>
                <div className="w-full rounded-lg bg-slate-50 border border-slate-200 p-5 text-slate-700 text-sm leading-relaxed font-medium shadow-inner print:bg-slate-50">
                    {inspection.remarks}
                </div>
                </div>
            )}
            
            {/* Signatures */}
            <div className="border-t border-slate-100 pt-8 mt-4">
              <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-8">Verification & Signatures</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                <div className="break-inside-avoid">
                  <label className="block text-[10px] font-bold text-slate-500 mb-4 uppercase tracking-widest text-center">Factory Rep</label>
                  <div className="flex flex-col items-center gap-4">
                        <div className="h-32 w-full max-w-xs rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-center p-4 bg-white shadow-inner">
                            {inspection.factory_rep_signature_url ? (
                                <img src={inspection.factory_rep_signature_url} alt="Factory Signature" className="max-h-full max-w-full object-contain" />
                            ) : (
                                <span className="text-slate-300 italic text-sm">No signature captured</span>
                            )}
                        </div>
                        <div className="text-center">
                            <div className="font-bold text-slate-900 text-base">Authorized Signature</div>
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Production Head / Factory QC</div>
                        </div>
                  </div>
                </div>
                <div className="break-inside-avoid">
                  <label className="block text-[10px] font-bold text-slate-500 mb-4 uppercase tracking-widest text-center">QA Inspector</label>
                  <div className="flex flex-col items-center gap-4">
                        <div className="h-32 w-full max-w-xs rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-center p-4 bg-white shadow-inner">
                            {inspection.inspector_signature_url ? (
                                <img src={inspection.inspector_signature_url} alt="Inspector Signature" className="max-h-full max-w-full object-contain" />
                            ) : (
                                <span className="text-slate-300 italic text-sm">No signature captured</span>
                            )}
                        </div>
                        <div className="text-center">
                            <div className="font-bold text-slate-900 text-base">{inspection.inspector_name}</div>
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">QC Inspector</div>
                        </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer Branding for Print */}
      <div className="mt-12 pt-8 border-t border-slate-100 flex items-center justify-between text-[10px] font-bold text-slate-400 uppercase tracking-widest px-2">
        <span>© {new Date().getFullYear()} QA MATE. All rights reserved.</span>
        <span className="print:block hidden">System-Generated Document</span>
      </div>

      {/* PDF Generation Modal — premium animated */}
      {generatingPDF && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/70 backdrop-blur-md p-4 animate-in fade-in duration-300 print:hidden">
          <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 animate-in zoom-in-95 slide-in-from-bottom-4 duration-500">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-400 via-blue-500 to-emerald-400 bg-[length:200%_100%] animate-gradient-x" />
            <div className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-emerald-300/30 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-blue-300/30 blur-3xl" />
            <div className="relative p-8 flex flex-col items-center text-center">
              <div className="relative mb-6">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-emerald-400 to-blue-500 blur-xl opacity-50 animate-pulse" />
                <div className="relative h-20 w-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center shadow-lg shadow-emerald-500/40">
                  <span className="material-symbols-outlined text-white text-[40px] animate-pulse">picture_as_pdf</span>
                </div>
                <div className="absolute inset-0 animate-spin" style={{ animationDuration: '3s' }}>
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 h-3 w-3 rounded-full bg-emerald-500 shadow-md shadow-emerald-500/50" />
                </div>
              </div>
              <h2 className="text-2xl font-black text-slate-900 tracking-tight mb-2">Generating your PDF</h2>
              <p className="text-sm text-slate-500 font-medium leading-relaxed mb-6 max-w-xs">
                We're crafting a beautifully formatted line inspection report. This usually takes just a few seconds.
              </p>
              <div className="w-full bg-slate-50 rounded-xl border border-slate-100 px-4 py-3 mb-6 overflow-hidden">
                <div className="flex items-center gap-3">
                  <div className="relative flex h-2 w-2 shrink-0">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                  </div>
                  <p key={pdfStatusIndex} className="text-sm font-bold text-slate-700 text-left flex-1 animate-in fade-in slide-in-from-bottom-1 duration-500">
                    {pdfStatusMessages[pdfStatusIndex]}
                  </p>
                </div>
              </div>
              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full w-1/3 bg-gradient-to-r from-emerald-500 to-blue-500 rounded-full animate-pdf-progress" />
              </div>
              <p className="mt-6 text-[10px] uppercase tracking-widest font-bold text-slate-400">
                Powered by <span className="text-emerald-600">QA Mate</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ───── Download Images Modal — premium animated ───── */}
      {downloadingImages && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/70 backdrop-blur-md p-4 animate-in fade-in duration-300 print:hidden">
          <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 animate-in zoom-in-95 slide-in-from-bottom-4 duration-500">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-violet-400 via-fuchsia-500 to-violet-400 bg-[length:200%_100%] animate-gradient-x" />
            <div className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-violet-300/30 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-fuchsia-300/30 blur-3xl" />
            <div className="relative p-8 flex flex-col items-center text-center">
              <div className="relative mb-6">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-violet-400 to-fuchsia-500 blur-xl opacity-50 animate-pulse" />
                <div className="relative h-20 w-20 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center shadow-lg shadow-violet-500/40">
                  <span className="material-symbols-outlined text-white text-[40px] animate-pulse">photo_library</span>
                </div>
                <div className="absolute inset-0 animate-spin" style={{ animationDuration: '3s' }}>
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 h-3 w-3 rounded-full bg-violet-500 shadow-md shadow-violet-500/50" />
                </div>
              </div>
              <h2 className="text-2xl font-black text-slate-900 tracking-tight mb-2">Downloading Images</h2>
              <p className="text-sm text-slate-500 font-medium leading-relaxed mb-6 max-w-xs">
                Fetching and packaging all report images into a neatly organised ZIP folder.
              </p>
              <div className="w-full bg-slate-50 rounded-xl border border-slate-100 px-4 py-3 mb-6 overflow-hidden">
                <div className="flex items-center gap-3">
                  <div className="relative flex h-2 w-2 shrink-0">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-violet-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-violet-500" />
                  </div>
                  <p key={imageStatusIndex} className="text-sm font-bold text-slate-700 text-left flex-1 animate-in fade-in slide-in-from-bottom-1 duration-500">
                    {imageStatusMessages[imageStatusIndex]}
                  </p>
                </div>
              </div>
              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full w-1/3 bg-gradient-to-r from-violet-500 to-fuchsia-500 rounded-full animate-pdf-progress" />
              </div>
              <p className="mt-6 text-[10px] uppercase tracking-widest font-bold text-slate-400">
                Powered by <span className="text-violet-600">QA Mate</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ───── PDF Template Selector Modal ───── */}
      {pdfModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4 print:hidden"
          onClick={() => !generatingPDF && setPdfModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md overflow-hidden animate-in zoom-in-95 fade-in duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-emerald-600 text-[22px]">picture_as_pdf</span>
                <h3 className="text-base font-bold text-slate-900">Generate PDF Report</h3>
              </div>
              <button
                onClick={() => setPdfModalOpen(false)}
                disabled={generatingPDF}
                className="text-slate-400 hover:text-slate-600 disabled:opacity-30"
              >
                <span className="material-symbols-outlined text-[22px]">close</span>
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Template</label>
                <div className="relative" ref={templateDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsTemplateDropdownOpen(prev => !prev)}
                    disabled={generatingPDF}
                    className="w-full flex items-center gap-2 rounded-lg border border-slate-200 bg-white py-2.5 pl-3 pr-9 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors outline-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <span className={`h-6 w-6 rounded-full ${selectedTemplateOption.iconBg} flex items-center justify-center shrink-0`}>
                      <span className={`material-symbols-outlined text-[14px] ${selectedTemplateOption.iconColor}`}>
                        {selectedTemplateOption.icon}
                      </span>
                    </span>
                    <span className="truncate">{selectedTemplateOption.name}</span>
                    <span className="material-symbols-outlined pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[18px] text-slate-400">expand_more</span>
                  </button>
                  {isTemplateDropdownOpen && (
                    <div className="absolute left-0 right-0 top-full mt-1 z-20 rounded-xl border border-slate-200 bg-white shadow-lg py-1 overflow-hidden">
                      {visibleTemplateOptions.map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => { setSelectedTemplate(opt.value); setIsTemplateDropdownOpen(false); }}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-blue-50 transition-colors text-left ${selectedTemplate === opt.value ? 'bg-blue-50 text-[#136bce] font-semibold' : 'text-slate-700'}`}
                        >
                          <span className={`h-6 w-6 rounded-full ${opt.iconBg} flex items-center justify-center shrink-0`}>
                            <span className={`material-symbols-outlined text-[14px] ${opt.iconColor}`}>{opt.icon}</span>
                          </span>
                          {opt.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-[11px] text-slate-500 mt-2 leading-snug">{selectedTemplateOption.description}</p>
              </div>

              {generatingPDF && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 flex items-center gap-2">
                  <span className="material-symbols-outlined text-emerald-600 text-[18px] animate-spin">progress_activity</span>
                  <span className="text-xs font-medium text-emerald-800">{pdfStatusMessages[pdfStatusIndex]}</span>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-2">
              <button
                onClick={() => setPdfModalOpen(false)}
                disabled={generatingPDF}
                className="rounded-lg px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-200 transition-colors disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await handleGeneratePDF(selectedTemplate);
                  setPdfModalOpen(false);
                }}
                disabled={generatingPDF}
                className="flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2 text-sm font-bold text-white shadow-md shadow-emerald-500/30 hover:bg-emerald-700 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className="material-symbols-outlined text-[18px]">
                  {generatingPDF ? 'hourglass_top' : 'picture_as_pdf'}
                </span>
                {generatingPDF ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Preview Modal */}
      {selectedDocument && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 print:hidden">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => setSelectedDocument(null)}
          />
          <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200 flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between p-4 border-b border-slate-100 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-[#136bce]">
                  <span className="material-symbols-outlined">description</span>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-slate-900">{selectedDocument.name}</h4>
                  <p className="text-[10px] text-slate-500 font-medium">Document Preview</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a 
                  href={selectedDocument.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-blue-50 hover:text-[#136bce] transition-all"
                  title="Download File"
                >
                  <span className="material-symbols-outlined text-[20px]">download</span>
                </a>
                <button
                  onClick={() => setSelectedDocument(null)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-red-50 hover:text-red-500 transition-all"
                  title="Close"
                >
                  <span className="material-symbols-outlined text-[20px]">close</span>
                </button>
              </div>
            </div>
            <div className="p-4 bg-slate-50 flex-1 overflow-auto flex flex-col items-center justify-center min-h-[300px] text-center rounded-b-2xl">
              {/\.(jpg|jpeg|png|gif|webp|svg)($|\?)/i.test(selectedDocument.name) ? (
                <img src={selectedDocument.url} alt={selectedDocument.name} className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-sm border border-slate-200 bg-white" />
              ) : /\.(pdf)($|\?)/i.test(selectedDocument.name) ? (
                <iframe src={selectedDocument.url} title={selectedDocument.name} className="w-full h-[70vh] rounded-lg border border-slate-200 shadow-sm bg-white"></iframe>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-20 h-20 rounded-full bg-white shadow-sm flex items-center justify-center mb-4 border border-slate-100">
                    <span className="material-symbols-outlined text-[40px] text-slate-300">draft</span>
                  </div>
                  <h5 className="text-base font-bold text-slate-800 mb-2">Detailed Preview Not Available</h5>
                  <p className="text-xs text-slate-500 max-w-xs leading-relaxed">
                    The file <span className="font-bold text-slate-700">{selectedDocument.name}</span> cannot be previewed directly in the browser window.
                  </p>
                  <div className="mt-8 flex gap-3">
                    <a 
                      href={selectedDocument.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-6 py-2 bg-[#136bce] text-white text-xs font-bold rounded-xl hover:bg-[#0e52a0] transition-all shadow-md active:scale-95 flex items-center gap-2"
                    >
                      <span className="material-symbols-outlined text-[18px]">download</span>
                      Download File
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/90 backdrop-blur-sm p-4 animate-in fade-in duration-200 print:hidden"
          onClick={() => { setPreviewImage(null); setZoom(1); }}
        >
          {/* Close */}
          <button 
            className="absolute top-6 right-6 text-white hover:text-slate-300 transition-colors p-2 rounded-full bg-slate-800/50 z-10"
            onClick={(e) => { e.stopPropagation(); setPreviewImage(null); setZoom(1); }}
          >
            <span className="material-symbols-outlined text-[32px]">close</span>
          </button>

          {/* Zoom controls */}
          <div 
            className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-slate-900/80 backdrop-blur-md border border-white/10 rounded-full px-4 py-2 z-10"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="text-white/70 hover:text-white transition-colors disabled:opacity-30"
              onClick={() => setZoom((prev: number) => Math.max(0.25, parseFloat((prev - 0.25).toFixed(2))))}
              disabled={zoom <= 0.25}
            >
              <span className="material-symbols-outlined text-[22px]">remove</span>
            </button>
            <span className="text-white text-xs font-bold tracking-widest w-12 text-center select-none">
              {Math.round(zoom * 100)}%
            </span>
            <button 
              className="text-white/70 hover:text-white transition-colors disabled:opacity-30"
              onClick={() => setZoom((prev: number) => Math.min(5, parseFloat((prev + 0.25).toFixed(2))))}
              disabled={zoom >= 5}
            >
              <span className="material-symbols-outlined text-[22px]">add</span>
            </button>
            <div className="w-px h-4 bg-white/20 mx-1" />
            <button 
              className="text-white/70 hover:text-white transition-colors"
              onClick={() => setZoom(1)}
            >
              <span className="material-symbols-outlined text-[20px]">fit_screen</span>
            </button>
          </div>

          {/* Image container — wheel listener attached via ref */}
          <div 
            ref={zoomContainerRef}
            className="flex items-center justify-center overflow-hidden max-h-[90vh] max-w-[90vw]"
            style={{ cursor: zoom > 1 ? 'grab' : 'zoom-in' }}
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={previewImage} 
              alt="Preview" 
              className="rounded-lg shadow-2xl animate-in zoom-in-95 duration-300 max-h-[82vh] max-w-[88vw] object-contain" 
              style={{ transform: `scale(${zoom})`, transformOrigin: 'center', transition: 'transform 0.1s ease' }}
              draggable={false}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LineInspectionReportDetail;
